import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import {
  gradient,
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
  zIndex,
  layout,
  duration,
  easing,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { m } from '../../motion';

// The floating Lobbero IQ chat. gradient.aurora is the reserved AI mark (design
// system §gradient); it appears on the FAB and the panel header and nowhere the
// rest of the app can reach. Two shells share one inner layout: a corner panel
// (float) and a centred sheet (full). Both shells are motion elements: the
// corner panel rises as a 'panel', the full one grows as a 'modal' (a
// 'takeover' on a phone), and each leaves the way it came - the motion module
// owns the numbers; the bespoke rise/fade keyframes this file carried are gone.

// Shared chrome: a column of header / context bar / scrolling thread / composer.
const panelChrome = css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xl};
`;

/* Each viewport-unit line below is written TWICE on purpose: the `vh`/`vw` line
 * is the fallback and the `dvh`/`dvw` line immediately overrides it wherever
 * dynamic units are understood. On iOS, `vh` is the LARGE viewport — the height
 * with the browser chrome hidden — so a panel sized in `vh` extends behind the
 * URL bar and the bottom toolbar, and the composer row at the bottom of this
 * panel is exactly what ends up underneath them. `dvw` likewise excludes the
 * desktop scrollbar that `vw` counts. design_system.md §8 rule 7. */
export const FloatPanel = m.div`
  ${panelChrome}
  position: fixed;
  right: ${spacing.md};
  bottom: ${spacing.md};
  z-index: ${zIndex.modal};
  width: min(440px, calc(100vw - ${spacing.md} * 2));
  width: min(440px, calc(100dvw - ${spacing.md} * 2));
  height: min(680px, calc(100vh - ${spacing.xxl} * 2));
  height: min(680px, calc(100dvh - ${spacing.xxl} * 2));

  ${down('sm')} {
    right: ${spacing.sm};
    left: ${spacing.sm};
    bottom: ${spacing.sm};
    width: auto;
    height: min(640px, calc(100vh - ${spacing.xl}));
    height: min(640px, calc(100dvh - ${spacing.xl}));
  }
`;

export const FullOverlay = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  background: ${palette.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.lg};

  ${down('sm')} {
    padding: 0;
  }
`;

export const FullPanel = m.div`
  ${panelChrome}
  width: min(980px, 100%);
  height: min(92vh, 940px);
  height: min(92dvh, 940px);

  /* 100% OF THE OVERLAY, not of the viewport. FullOverlay is
   * position: fixed / inset: 0 with its padding dropped at this width, and a
   * fixed element is already sized to the SMALL viewport on iOS — the height
   * that excludes the browser chrome. Percentages of it are therefore exactly
   * right on every browser and need no dvh fallback pair at all, which is why
   * this is the preferred shape wherever a positioned ancestor exists. The
   * previous 100vh overflowed behind Safari's toolbars and took the composer
   * with it.
   *
   * NO BACKTICKS IN THIS COMMENT. It is inside a styled template literal, so
   * one would silently terminate the string and the file stops parsing. */
  ${down('sm')} {
    width: 100%;
    height: 100%;
    border-radius: 0;
    border: none;
  }
`;

export const Header = styled.div`
  flex-shrink: 0;
  background: ${gradient.aurora};
  color: ${palette.white};
  padding: ${spacing.sm} ${spacing.md};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base}px;
  flex: 1;
  min-width: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const IconBtn = styled.button`
  -webkit-appearance: none;
  appearance: none;
  border: none;
  background: rgba(255, 255, 255, 0.16);
  color: ${palette.white};
  width: 32px;
  height: 32px;
  /* Dense at a mouse on purpose — these sit in the chat panel's slim aurora
     header, where a 44px pair would double its height. Under a thumb they are
     the expand and close controls of a full-screen panel, so they take the
     44px minimum there (design_system rule 5). */
  ${touch} {
    width: ${layout.touchTarget};
    height: ${layout.touchTarget};
  }
  border-radius: ${radius.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.size.md}px;
  line-height: 1;
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.standard};
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

// The scope + timeframe strip. Always visible so the reader knows exactly WHAT
// this handover is about: portfolio, building, who is asking, and over which shift.
export const ContextBar = styled.div`
  flex-shrink: 0;
  background: ${palette.surfaceMuted};
  border-bottom: 1px solid ${palette.line};
  padding: ${spacing.xs} ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const ScopeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.half} ${spacing.sm};
`;

export const ScopeChip = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing.half};
  min-width: 0;
`;

export const ScopeLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  text-transform: uppercase;
  letter-spacing: ${typography.tracking.wide};
  color: ${palette.slateLight};
`;

export const ScopeValue = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

export const TimeframeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.half};
`;

export const Select = styled.select`
  -webkit-appearance: none;
  appearance: none;
  height: ${layout.compactControlHeight};
  padding: 0 ${spacing.md} 0 ${spacing.xs};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  cursor: pointer;
  max-width: 200px;
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const DateInput = styled.input`
  height: ${layout.compactControlHeight};
  padding: 0 ${spacing.xs};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const Thread = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  background: ${palette.porcelain};
`;

export const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const WelcomeText = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  line-height: ${typography.leading.normal};
  margin: 0;
`;

export const QBubble = styled.div`
  align-self: flex-end;
  max-width: 85%;
  background: ${status.brand.dot};
  color: ${palette.white};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.md};
  border-bottom-right-radius: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  white-space: pre-wrap;
`;

// $wide: a LIST answer is a table, not a sentence, and 90% of a chat column is
// not enough for seven parcel columns. The transient prop keeps every existing
// call site (errors, the loading placeholder, prose answers) byte-identical.
export const ABubble = styled.div<{ $wide?: boolean }>`
  align-self: flex-start;
  max-width: ${p => (p.$wide ? '100%' : '90%')};
  width: ${p => (p.$wide ? '100%' : 'auto')};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.inkSoft};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.md};
  border-bottom-left-radius: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

// A handover card is a full-width assistant turn, not a chat bubble.
export const CardTurn = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const SysNote = styled.p`
  align-self: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  text-align: center;
  margin: 0;
`;

export const Composer = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${palette.line};
  background: ${palette.surface};
  padding: ${spacing.sm} ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const Starters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
`;

export const Chip = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${status.brand.bg};
  color: ${status.brand.fg};
  border: none;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  cursor: pointer;
  &:hover:not(:disabled) {
    filter: brightness(0.97);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const InputRow = styled.div`
  display: flex;
  gap: ${spacing.xs};
  align-items: flex-end;
`;

export const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: ${layout.fieldHeight};
  max-height: 120px;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  /* WP-40. THERE WAS NO DISABLED STATE AT ALL — a disabled composer was pixel
     for pixel a live one. That is what produced "I typed a question and nothing
     happened": the field was correctly disabled the whole time and said so
     nowhere. The reason text beside it is the other half; this is the half that
     stops a reader typing into a dead box in the first place. */
  &:disabled {
    background: ${palette.surfaceMuted};
    border-color: ${palette.line};
    color: ${palette.slate};
    cursor: not-allowed;
  }
  &:disabled::placeholder {
    color: ${palette.slate};
  }
`;

export const Counter = styled.p<{ $over?: boolean }>`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${p => (p.$over ? palette.alert : palette.slateLight)};
  margin: 0;
  text-align: right;
`;

export const Empty = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  margin: 0;
  padding: ${spacing.md};
  text-align: center;
`;

/* ── THE THINKING INDICATOR (decision D7, 2026-08-20) ────────────────────────
   Three dots that rise in sequence while an answer is in flight. It replaces a
   static "Checking the log…", which sat there unchanged for however long the
   model took — indistinguishable, after a few seconds, from a page that had
   stopped.

   The text stays underneath it as the accessible name. A screen-reader user
   gets a sentence, not three animated dots that read as nothing; this is why
   the label is visually hidden rather than deleted.

   REDUCED MOTION IS NOT A NICETY HERE. Three things pulsing in the corner is
   exactly the pattern that triggers vestibular symptoms, so under
   prefers-reduced-motion the dots hold still and the label carries the meaning
   on its own.

   NO BACKTICKS IN THESE COMMENTS: they are inside styled template literals. */
const thinkingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
  40%           { transform: translateY(-4px); opacity: 1; }
`;

export const ThinkingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 1em;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${palette.slate};
    animation: ${thinkingBounce} 1.4s ease-in-out infinite both;
  }

  span:nth-of-type(2) {
    animation-delay: 0.16s;
  }

  span:nth-of-type(3) {
    animation-delay: 0.32s;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      opacity: 0.6;
    }
  }
`;

/* The sentence the dots stand in for. Present for assistive technology, not
   drawn — the clip-path recipe rather than display:none, which would take it
   out of the accessibility tree along with everything else. */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
